import { VisuallyHidden } from '@react-aria/visually-hidden';
import { Component, ReactNode } from 'react';
import { Location } from 'react-router-dom';

import { PageError } from './PageError';

export interface PageErrorBoundaryProps {
  location: Location;
  children: ReactNode;
}

export interface PageErrorBoundaryState {
  hasError: boolean;
  location: Location | null;
}

/** Wraps the pages in the authenticated app to provide a UI and logging when a page-level error occurs. */
export class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, location: null };
  }

  static getDerivedStateFromProps(props: PageErrorBoundaryProps, state: PageErrorBoundaryState) {
    // The error boundary needs to be reset to not show when the location changes.
    if (props.location !== state.location) {
      return { hasError: false, location: props.location };
    }
    return state;
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  // biome-ignore lint/suspicious/noExplicitAny: TODO: fix
  componentDidCatch(error: any) {
    // eslint-disable-next-line no-console
    console.error(error);
    // TODO log the error to a logging service
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      // TODO Get a proper design for this page error boundary UI.
      return (
        <main>
          <VisuallyHidden>
            <h1 role="alert">Page could not be loaded</h1>
          </VisuallyHidden>
          <PageError error={500} />
        </main>
      );
    }
    return children;
  }
}
